/* Rebolt adapter. Country detection and region rules belong to variant-geo-filter.js. */
(() => {
  if (window.reboltGeoFilterLoaded) return;
  window.reboltGeoFilterLoaded = true;

  const CARD = '.RB_Product_Card';
  const SELECT = 'select.RB_PC_Selector';
  const BUY = '.RB_Product_AddToCart, .RB_FBT_AddToCart, .RB_Quantity_AddToCart, .RB_BuildBox_AddToCart';
  const requests = new Map();
  const pendingRequests = new Set();
  const roots = new Map();
  const cards = new WeakMap();
  const selects = new WeakMap();
  const api = () => window.variantGeoFilter;
  const country = () => api()?.getCountryCode();

  function productURL(card) {
    const link = card.querySelector('.RB_PC_Title a[href], .RB_PC_Media a[href]');
    if (!link) return null;
    try {
      const path = new URL(link.href, location.href).pathname;
      const handle = path.match(/\/products\/([^/]+)/)?.[1];
      if (!handle) return null;
      const base = window.Shopify?.routes?.root || '/';
      return new URL(`${base}products/${encodeURIComponent(decodeURIComponent(handle))}`, location.origin);
    } catch (_) { return null; }
  }

  function selectionSignature(card) {
    return JSON.stringify([country(), productURL(card)?.pathname,
      [...card.querySelectorAll(SELECT)].map(select => select.value)]);
  }

  function reportedVariantIds(card, data, root) {
    if (!Array.isArray(root.selection)) return [];
    const index = card.getAttribute('data-index');
    const entry = root.selection.find(item => index !== null ? String(item.index) === index :
      String(item.productId) === data.element.dataset.reboltGeoProduct);
    if (!entry) return [];
    return entry.variants ? entry.variants.map(item => item.variantId || item.id).filter(Boolean).map(String) :
      entry.variantId ? [String(entry.variantId)] : [];
  }

  function metadata(url) {
    const key = url.pathname;
    if (requests.has(key)) return requests.get(key);
    const request = (async () => {
      const endpoint = new URL(url);
      endpoint.searchParams.set('section_id', 'rebolt-geo-data');
      const response = await fetch(endpoint, { credentials: 'same-origin', signal: AbortSignal.timeout(10000) });
      if (!response.ok) throw new Error('Metadata request failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const element = doc.querySelector('[data-rebolt-geo-product]');
      const rules = JSON.parse(element?.querySelector('[data-geo-variants]')?.textContent || 'null');
      const names = JSON.parse(element?.getAttribute('data-rebolt-geo-options') || 'null');
      const flags = ['usHide', 'canadaHide', 'ukHide', 'europeHide', 'restHide'];
      if (!element?.dataset.reboltGeoProduct || !Array.isArray(names) || !names.length ||
          !Array.isArray(rules) || !rules.length || rules.some(rule =>
            !rule.id || flags.some(flag => typeof rule[flag] !== 'boolean'))) {
        throw new Error('Incomplete geo metadata');
      }
      return { element, rules, names, byId: new Map(rules.map(rule => [String(rule.id), rule])) };
    })();
    // Cache both successful and failed requests. A merchant/customer can explicitly retry failures.
    requests.set(key, request);
    pendingRequests.add(key);
    request.then(() => pendingRequests.delete(key), () => pendingRequests.delete(key));
    return request;
  }

  function setStatus(card, state, status) {
    if (status === 'pending' && state.loadingTimedOut) status = 'error';
    state.status = status;
    card.querySelectorAll('.RB_PC_Variants').forEach(picker => {
      picker.toggleAttribute('data-rebolt-geo-ready', status === 'ready');
      picker.setAttribute('aria-busy', String(status === 'pending'));
    });
    if (status === 'pending' && !state.loadingTimer) {
      state.loadingTimer = setTimeout(() => {
        state.loadingTimer = null;
        if (!card.isConnected || state.status !== 'pending') return;
        state.loadingTimedOut = true;
        setStatus(card, state, 'error');
        roots.get(card.closest('[data-bundleid]'))?.observer.takeRecords();
      }, 2500);
    } else if (status !== 'pending') {
      clearTimeout(state.loadingTimer);
      state.loadingTimer = null;
      if (status === 'ready') state.loadingTimedOut = false;
    }
    let message = card.querySelector('[data-rebolt-geo-message]');
    if (status === 'ready') { message?.remove(); return; }
    const text = status === 'error' ? 'Regional availability could not be verified.' :
      status === 'unavailable' ? 'No bundle options are available in your region.' : 'Checking regional availability…';
    if (message?.dataset.status === status) return;
    message ||= document.createElement('div');
    message.setAttribute('data-rebolt-geo-message', '');
    message.setAttribute('role', 'status');
    message.dataset.status = status;
    message.textContent = text;
    if (status === 'error') {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = 'Retry';
      retry.addEventListener('click', () => {
        if (state.key && !pendingRequests.has(state.key)) requests.delete(state.key);
        state.loadingTimedOut = false;
        setStatus(card, state, 'pending');
        schedule(card.closest('[data-bundleid]'));
      });
      message.append(' ', retry);
    }
    card.append(message);
  }

  function restoreOptions(select, key) {
    let state = selects.get(select);
    if (!state || state.key !== key) {
      state = { key, removed: [] };
      selects.set(select, state);
    }
    const value = select.value;
    select.querySelector('[data-rebolt-geo-placeholder]')?.remove();
    for (const { option, index } of state.removed) {
      // Rebolt may already have replaced an option with a fresh node.
      if (![...select.options].some(current => current.value === option.value)) {
        select.insertBefore(option, select.options[index] || null);
      }
    }
    state.removed = [];
    select.value = value;
    return state;
  }

  function unavailablePlaceholder(select) {
    let placeholder = select.querySelector('option[value=""]');
    if (!placeholder) {
      placeholder = new Option('Unavailable in your region', '');
      placeholder.disabled = true;
      placeholder.setAttribute('data-rebolt-geo-placeholder', '');
      select.append(placeholder);
    }
    select.value = '';
  }

  function removeOptions(select, state, restricted) {
    [...select.options].forEach((option, index) => {
      if (option.value && restricted(option.value)) {
        state.removed.push({ option, index });
        option.remove();
      }
    });
    // Never alter disabled on Rebolt's own options or selectors.
    if (![...select.options].some(option => option.value)) {
      unavailablePlaceholder(select);
    }
  }

  function optionIndex(select, data) {
    const named = select.name.match(/_o_(\d+)$/);
    if (named && Number(named[1]) < data.names.length) return Number(named[1]);
    const legend = select.closest('.RB_PC_Variants')?.querySelector('legend');
    const label = [...(legend?.childNodes || [])].filter(node => node.nodeType === Node.TEXT_NODE)
      .map(node => node.textContent).join('').trim();
    return data.names.indexOf(label);
  }

  function filterCard(card, data, key, code) {
    const hidden = new Set([...api().getHiddenVariantIds(data.rules, code)].map(String));
    if (api().productIsHidden(data.element, code)) data.rules.forEach(rule => hidden.add(String(rule.id)));
    const controls = [...card.querySelectorAll(SELECT)];
    const permitted = data.rules.filter(rule => !hidden.has(String(rule.id)));
    if (!controls.length) return { status: permitted.length === data.rules.length ? 'ready' : 'unavailable' };
    let status = permitted.length ? 'ready' : 'unavailable';
    const changes = [];
    const groups = new Map();

    for (const select of controls) {
      const state = restoreOptions(select, key);
      const value = select.value;
      const index = optionIndex(select, data);
      if (index >= 0) {
        const optionKey = `option${index + 1}`;
        if ([...select.options].some(option => option.value && !data.rules.some(rule => rule[optionKey] === option.value))) status = 'error';
        removeOptions(select, state, optionValue => {
          const matching = data.rules.filter(rule => rule[optionKey] === optionValue);
          return matching.length > 0 && matching.every(rule => hidden.has(String(rule.id)));
        });
        // Keep separate option groups separate (Rebolt can render several quantity rows).
        let group = select.parentElement;
        while (group !== card && group.querySelectorAll(SELECT).length < data.names.length) group = group.parentElement;
        const list = groups.get(group) || [];
        list.push({ select, index, value });
        groups.set(group, list);
      } else {
        // The combined-variant layout uses Shopify variant IDs, not option labels.
        if ([...select.options].some(option => option.value && !data.byId.has(option.value))) status = 'error';
        removeOptions(select, state, id => hidden.has(id));
        if (value && hidden.has(value)) {
          const next = [...select.options].find(option => option.value && !option.disabled && data.byId.has(option.value));
          select.value = next?.value || '';
          if (next) changes.push({ select, value: next.value });
          else { unavailablePlaceholder(select); status = 'unavailable'; }
        }
      }
    }

    for (const group of groups.values()) {
      if (group.length !== data.names.length || new Set(group.map(item => item.index)).size !== data.names.length) {
        status = 'error';
        continue;
      }
      const selected = data.rules.find(rule => group.every(item => rule[`option${item.index + 1}`] === item.value));
      if (!selected && group.some(item => !item.value)) continue; // Preserve Rebolt's unselected placeholder.
      if (selected && !hidden.has(String(selected.id))) continue;
      const next = permitted.find(rule => group.every(({ select, index }) =>
        [...select.options].some(option => option.value === rule[`option${index + 1}`] && !option.disabled)));
      const item = next && group.find(item => item.value !== next[`option${item.index + 1}`]);
      if (item) {
        // Change one option at a time so Rebolt can recalculate dependent inventory/prices.
        changes.push({ select: item.select, value: next[`option${item.index + 1}`] });
      } else status = 'unavailable';
    }
    return { status: changes.length && status === 'ready' ? 'pending' : status, changes };
  }

  async function processCard(card, root) {
    let state = cards.get(card);
    if (!state) { state = { revision: 0 }; cards.set(card, state); }
    const revision = ++state.revision;
    const url = productURL(card);
    state.key = url?.pathname;
    const code = country();
    if (!code) { setStatus(card, state, 'pending'); root.observer.takeRecords(); return; }
    if (!url) { setStatus(card, state, 'error'); root.observer.takeRecords(); return; }
    if (state.signature !== selectionSignature(card)) setStatus(card, state, 'pending');
    root.observer.takeRecords();
    try {
      const data = await metadata(url);
      if (!card.isConnected || revision !== state.revision) return;
      const explicitId = card.getAttribute('data-pid');
      if (explicitId && explicitId !== data.element.dataset.reboltGeoProduct) throw new Error('Product metadata mismatch');
      const result = filterCard(card, data, url.pathname, code);
      const reported = reportedVariantIds(card, data, root);
      const reportSignature = JSON.stringify([code, reported]);
      const hidden = new Set([...api().getHiddenVariantIds(data.rules, code)].map(String));
      if (reported.some(id => !data.byId.has(id))) result.status = 'error';
      else if (reported.some(id => hidden.has(id)) && result.status !== 'unavailable') {
        // React can set a controlled select back to a removed value without adding DOM nodes.
        // Re-send the permitted selection once per app report, then await its corrected state.
        result.status = 'pending';
        result.changes ||= [];
        if (!result.changes.length && state.reportSignature !== reportSignature) {
          const select = card.querySelector(SELECT);
          if (select?.selectedOptions[0] && !select.selectedOptions[0].disabled && select.value) {
            result.changes.push({ select, value: select.value });
          }
        }
      }
      state.reportSignature = reportSignature;
      setStatus(card, state, result.status);
      state.signature = selectionSignature(card);
      root.observer.takeRecords(); // Ignore only our own option removals/messages.
      for (const { select, value } of result.changes || []) {
        if (!select.isConnected) continue;
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (result.changes?.length) {
        schedule(root.element);
      }
    } catch (_) {
      if (!card.isConnected || revision !== state.revision) return;
      setStatus(card, state, 'error');
      root.observer.takeRecords();
    }
  }

  function schedule(element) {
    const root = roots.get(element);
    if (!root || root.scheduled) return;
    // Mutation observers run before paint, including replacement of a select inside a ready fieldset.
    element.querySelectorAll('.RB_PC_Variants[data-rebolt-geo-ready]').forEach(picker => {
      picker.removeAttribute('data-rebolt-geo-ready');
    });
    root.scheduled = true;
    queueMicrotask(() => {
      root.scheduled = false;
      if (!element.isConnected) return;
      element.querySelectorAll(CARD).forEach(card => processCard(card, root));
    });
  }

  function discover(scope) {
    const found = [...scope.querySelectorAll(CARD)];
    if (scope.matches?.(CARD)) found.push(scope);
    found.forEach(card => {
      const element = card.closest('[data-bundleid]');
      if (!element || roots.has(element)) return;
      const root = { element, observer: new MutationObserver(() => schedule(element)) };
      roots.set(element, root);
      root.observer.observe(element, { childList: true, subtree: true, attributes: true,
        attributeFilter: ['disabled', 'selected', 'value', 'href', 'data-pid'] });
      element.addEventListener('change', () => schedule(element));
      schedule(element);
    });
  }

  // Capture before Rebolt's delegated click handler. Unknown/failed data must not authorize purchase.
  function guardPurchase(event) {
    const button = event.target.closest?.(BUY);
    const element = button?.closest('[data-bundleid]');
    if (!element) return;
    const pending = [...element.querySelectorAll(CARD)].find(card => {
      const state = cards.get(card);
      return state?.status !== 'ready' || state.signature !== selectionSignature(card);
    });
    if (!pending) return;
    schedule(element);
    event.preventDefault();
    event.stopImmediatePropagation();
    pending.querySelector('[data-rebolt-geo-message]')?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
  }
  document.addEventListener('click', guardPurchase, true);
  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' || event.key === ' ') guardPurchase(event);
  }, true);
  document.addEventListener('geo:ready', () => roots.forEach(root => schedule(root.element)));
  window.addEventListener('rebolt:selectedBundle', event => {
    roots.forEach(root => {
      const selection = event.detail?.selectedBundle?.[root.element.dataset.bundleid];
      if (selection !== undefined) {
        root.selection = selection;
      }
      schedule(root.element);
    });
  });

  discover(document);
  // Discovery only: filter each new subtree, never rescan the whole page after every mutation.
  new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'OPTION') discover(node);
    }
    roots.forEach((root, element) => {
      if (!element.isConnected) { root.observer.disconnect(); roots.delete(element); }
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
